import {
  ɵComponentType as ComponentType,
  ɵmarkDirty as markDirty,
} from '@angular/core';

import { isFunction } from 'lodash';

const isComponent = (
  compCtorOrNot: ComponentType<unknown> | unknown,
): compCtorOrNot is ComponentType<unknown> =>
  !!(compCtorOrNot as ComponentType<unknown>).ɵcmp;

/**
 * Angular component methods decorator for marking the component as dirty.
 * The same result can be achieved using {@link ChangeDetectorRef.markForCheck}() method.
 * The main advantage of using this component is that you don't need to inject {@link ChangeDetectorRef}.
 *
 * TODO: Test it after update to Angular 10. `.ɵcmp` doesn't work with unit tests in Angular 9.
 */
export const ngMarkForCheck = (): MethodDecorator => {
  // eslint-disable-line prefer-arrow/prefer-arrow-functions
  return <T>(
    target: object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>,
  ): TypedPropertyDescriptor<T> => {
    const originalMethod = descriptor?.value;
    if (!isFunction(originalMethod)) {
      throw new TypeError(
        `@ngMarkForCheck() decorator can be applied only for methods.`,
      );
    }

    const wrapper = function (...args: unknown[]): unknown {
      // eslint-disable-next-line no-invalid-this
      const res = originalMethod.apply(this, args);
      // eslint-disable-next-line no-invalid-this
      markDirty(this);

      return res;
    };

    let checker = function (...args: unknown[]): unknown {
      if (!isComponent(target.constructor)) {
        throw new TypeError(
          `@ngMarkForCheck() decorator can be used only inside Angular components`,
        );
      }
      newDescriptor.value = wrapper as any;
      checker = undefined;

      // eslint-disable-next-line no-invalid-this
      return wrapper.apply(this, args);
    };

    const newDescriptor = { ...descriptor, value: checker as any };

    return newDescriptor;
  };
};
